import React from "react";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { CustomerSearchClient } from "../../../../../../../Wrap/Api/Customer/CustomerSearch";
import { OrganisationSearchClient } from "../../../../../../../Wrap/Api/Customer/OrganisationSearch";
import { CustomerType } from "../../../../../../../Wrap/Constants/Enums/Customer/CustomerType";
import { LawType } from "../../../../../../../Wrap/Constants/Enums/Customer/LawType";
import { RelationshipType } from "../../../../../../../Wrap/Constants/Enums/Customer/RelationshipType";
import { CustomerStatus } from "../../../../../../../Wrap/Constants/Enums/Domain/CustomerStatus";
import { EntityType } from "../../../../../../../Wrap/Constants/Enums/EntityType";
import { RoleType } from "../../../../../../../Wrap/Constants/Enums/RoleType";
import { CustomerSearchModel } from "../../../../../../../Wrap/Models/Models/Customer/CustomerSearch/CustomerSearchModel";
import { Organisation } from "../../../../../../../Wrap/Models/Models/Customer/Organisation";
import { OrganisationMember } from "../../../../../../../Wrap/Models/Models/Customer/OrganisationMember";
import { OrganisationSearchModel } from "../../../../../../../Wrap/Models/Models/Customer/OrganisationSearch/OrganisationSearchModel";
import { CheckboxGroup } from "../../../../../../Shared/CheckboxGroup/CheckboxGroup";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { SearchInput } from "../../../../../../Shared/SearchInput/SearchInput";
import { Table } from "../../../../../../Shared/Table/Table";

import {
    RelationshipTypeArray,
    RelationshipTypeMap,
    CompanyTypeMap,
    OrganisationMemberPopUpInfo,
} from "../../Resource/Contents";
import { AddCorporateTrustee } from "./AddCorporateTrustee";
import { AddFullClient } from "./AddFullClient";

export interface ITrustMemberDetailsProps extends IProcessComponentProps {
    data: OrganisationMember;
    ownerAdviserId: number;
    customerType: CustomerType;
    lawToApply: LawType;
    selectedCustomers: number[];
    onSubmit: (journeyType) => void;
    disableSettlorOption?: boolean;
    disableBeneficiaryOption?: boolean;
    disableCorporateTrusteeOption?: boolean;
    existingMembers: OrganisationMember[];
}

interface ITrustMemberDetailsState {
    SearchValue: string;
    isOpen: boolean;
    showErrorMsg: any;
    hideResult: boolean;
}

export class TrustMemberDetails extends ProcessComponent<ITrustMemberDetailsProps, ITrustMemberDetailsState> {
    constructor(props: ITrustMemberDetailsProps) {
        super(props);
        this.state = {
            isOpen: false,
            SearchValue: null,
            showErrorMsg: null,
            hideResult: false,
        };
    }

    componentDidMount() {
        super.componentDidMount();
    }

    isValid(): boolean {
        const { data, lawToApply } = this.props;
        const flag = super.isValid();
        if (
            data?.Relationships?.length > 0 &&
            !data?.Relationships?.includes(RelationshipType.CorporateTrustee) &&
            !data?.Individual
        ) {
            this.props.showErrorMsgPopUp(["Please complete client details"], true);
            return false;
        }
        //individual trustee age validation
        if (
            data?.Individual?.PersonalDetails?.DateOfBirth &&
            data?.Relationships?.includes(RelationshipType.IndividualTrustee)
        ) {
            if (
                JsonDateParser.calculateAge(data?.Individual?.PersonalDetails?.DateOfBirth) < 16 &&
                lawToApply == LawType.ScottishLaw
            ) {
                this.props.showErrorMsgPopUp(["Individual trustee must be over 16 years old."], true);
                return false;
            } else if (
                JsonDateParser.calculateAge(data?.Individual?.PersonalDetails?.DateOfBirth) < 18 &&
                lawToApply != LawType.ScottishLaw
            ) {
                this.props.showErrorMsgPopUp(["Individual trustee must be over 18 years old."], true);
                return false;
            }
        }
        //check at least one new relationship has been added on the customer
        const existingRelationships = this.getCustomerExistingRelationships();
        if (!(data?.Relationships || []).some((rel) => !existingRelationships.includes(rel))) {
            this.props.showErrorMsgPopUp(["Please select a role for this Trust member"], true);
            return false;
        }
        if (
            existingRelationships.length == 0 &&
            data?.Relationships.every((ele) => ele == RelationshipType.Beneficiary)
        ) {
            this.props.showErrorMsgPopUp(["Can not add a trust member as beneficiary only here"], true);
            return false;
        }
        return flag;
    }

    private transformIndividualInputSearch(data: CustomerSearchModel) {
        const result = data?.CustomerList?.filter((x) => !this.props.selectedCustomers.includes(x.CustomerId));

        if (result.length > 0) {
            const customeChildren = (
                <Table
                    data={result}
                    col={[
                        {
                            title: "Client",
                            key: "Name",
                            render: (data, row, index) => {
                                return <span>{row.FirstName + " " + row.LastName}</span>;
                            },
                        },
                        {
                            title: "Date of birth",
                            key: "DateOfBirth",
                            render: (data, row, index) => {
                                return JsonDateParser.getFormatDateString(new Date(data), "dd MM yyyy", true);
                            },
                        },
                        {
                            title: "",
                            key: "CustomerId",
                            render: (data, row, index) => {
                                return (
                                    <button
                                        className="secondary-add-button"
                                        onClick={() => {
                                            this.props.onChange({ CustomerId: data });
                                            this.setHideSearchCustomerResult();
                                        }}
                                    >
                                        <span className="text">+ add client</span>
                                    </button>
                                );
                            },
                        },
                    ]}
                    showEmptyMsg={true}
                />
            );
            return this.state.hideResult ? null : customeChildren;
        } else {
            return (
                <ul id="smart-search" className="ui-menu ui-autocomplete ui-front mt-50">
                    <li className="ui-menu-item" key={`no-result`} id={`smart-search-${0}`}>
                        <div id={`ui-id-${0}`} className="ui-menu-item-wrapper">
                            No results Found
                        </div>
                    </li>
                </ul>
            );
        }
    }

    private transformOrganisationInputSearch(data: OrganisationSearchModel) {
        const result = data?.PageOfResults?.filter((x) => !this.props.selectedCustomers.includes(x.CustomerId));
        if (result.length > 0) {
            const customeChildren = (
                <Table
                    data={result}
                    col={[
                        {
                            title: "Company name",
                            key: "Name",
                            render: (data, row, index) => {
                                return <span>{data}</span>;
                            },
                        },
                        {
                            title: "Company type",
                            key: "CompanyType",
                            render: (data, row, index) => {
                                return <span>{CompanyTypeMap[data]}</span>;
                            },
                        },
                        {
                            title: "",
                            key: "CustomerId",
                            render: (data, row: Organisation, index) => {
                                return (
                                    <button
                                        className="secondary-add-button"
                                        onClick={() => {
                                            this.props.onChange({
                                                CustomerId: data,
                                            });
                                            this.setHideSearchCustomerResult();
                                            // this.onDataChange;
                                        }}
                                    >
                                        <span className="text">+ add client</span>
                                    </button>
                                );
                            },
                        },
                    ]}
                    showEmptyMsg={true}
                />
            );
            return this.state.hideResult ? null : customeChildren;
        } else {
            return (
                <ul id="smart-search" className="ui-menu ui-autocomplete ui-front mt-50">
                    <li className="ui-menu-item" key={`no-result`} id={`smart-search-${0}`}>
                        <div id={`ui-id-${0}`} className="ui-menu-item-wrapper">
                            No results Found
                        </div>
                    </li>
                </ul>
            );
        }
    }

    private renderIndividualDetails() {
        const { data, ownerAdviserId } = this.props;
        const client = new CustomerSearchClient();

        return (
            <>
                {data?.ExistingCustomer == true && (
                    <SearchInput
                        apiClient={(v) => {
                            return client.get(
                                null,
                                CustomerStatus.Active,
                                v + "%",
                                null,
                                null,
                                null,
                                null,
                                RoleType.NotSpecified,
                                ownerAdviserId,
                                EntityType.Advisor,
                                ownerAdviserId,
                                1,
                                10
                            );
                        }}
                        onInputFocus={() => this.setHideSearchCustomerResult(false)}
                        minLength={3}
                        customeChildren={(data) => this.transformIndividualInputSearch(data)}
                        cssWrapper="control-size-430"
                    />
                )}

                {(data?.ExistingCustomer == false || data?.CustomerId != null) && (
                    <AddFullClient
                        data={data?.Individual}
                        customerId={data?.CustomerId}
                        existingCustomer={data?.ExistingCustomer}
                        ownerAdviserId={ownerAdviserId}
                        onChange={(changes) => this.props.onChange({ Individual: changes })}
                        onCustomerIdChange={(customerId) =>
                            this.onDataChange(this.props.onChange, customerId, data, "CustomerId")
                        }
                        onRef={(ref) => {
                            this.appendChild(ref);
                        }}
                        onSetAdditionalDetails={(customerId, existingCustomer) => {
                            this.props.onChange({ CustomerId: customerId, ExistingCustomer: existingCustomer });
                        }}
                        showErrorMsgPopUp={(msg, showNow) => this.props.showErrorMsgPopUp(msg, showNow)}
                    />
                )}
            </>
        );
    }

    private renderCompanyDetails() {
        const { data, ownerAdviserId } = this.props;
        const client = new OrganisationSearchClient();
        return (
            <>
                {data?.ExistingCustomer == true && (
                    <SearchInput
                        apiClient={(v) => {
                            return client.get(
                                null,
                                CustomerStatus.Active,
                                CustomerType.Company,
                                null,
                                null,
                                v,
                                true,
                                null,
                                null,
                                EntityType.Advisor,
                                ownerAdviserId,
                                ownerAdviserId,
                                1,
                                10
                            );
                        }}
                        onInputFocus={() => this.setHideSearchCustomerResult(false)}
                        minLength={3}
                        customeChildren={(data) => this.transformOrganisationInputSearch(data)}
                        cssWrapper="control-size-430"
                    />
                )}
                {(data?.ExistingCustomer == false || data?.CustomerId != null) && (
                    <AddCorporateTrustee
                        data={data?.Company}
                        ClientModel={data?.Individual}
                        OwnerAdviserId={ownerAdviserId}
                        existingCustomer={data?.ExistingCustomer}
                        customerId={data?.CustomerId}
                        onChange={(changes) => this.onDataChange(this.props.onChange, changes, data, "Company")}
                        onSetClientModel={(ClientModel) =>
                            this.onDataChange(this.props.onChange, ClientModel, data, "Individual")
                        }
                        onRef={(ref) => {
                            this.appendChild(ref);
                        }}
                        showErrorMsgPopUp={(msg, showNow) => this.props.showErrorMsgPopUp(msg, showNow)}
                    />
                )}
            </>
        );
    }

    private getConstraint(type: RelationshipType, existingRelationships: RelationshipType[]) {
        const { data, disableBeneficiaryOption, disableSettlorOption, disableCorporateTrusteeOption } = this.props;
        if (existingRelationships.includes(type)) {
            return true;
        }
        const selectedRelationships = data?.Relationships.concat(existingRelationships);
        if (type == RelationshipType.IndividualTrustee) {
            return selectedRelationships.includes(RelationshipType.CorporateTrustee);
        } else if (type == RelationshipType.CorporateTrustee) {
            if (disableCorporateTrusteeOption == true) {
                return true;
            } else {
                return (
                    selectedRelationships.includes(RelationshipType.Beneficiary) ||
                    selectedRelationships.includes(RelationshipType.Settlor) ||
                    selectedRelationships.includes(RelationshipType.IndividualTrustee)
                );
            }
        } else if (type == RelationshipType.Settlor) {
            if (disableSettlorOption == true) {
                return true;
            } else {
                return (
                    selectedRelationships.includes(RelationshipType.Beneficiary) ||
                    selectedRelationships.includes(RelationshipType.CorporateTrustee)
                );
            }
        } else if (type == RelationshipType.Beneficiary) {
            if (disableBeneficiaryOption == true) {
                return true;
            } else {
                return (
                    selectedRelationships.includes(RelationshipType.CorporateTrustee) ||
                    selectedRelationships.includes(RelationshipType.Settlor) ||
                    !selectedRelationships.includes(RelationshipType.IndividualTrustee)
                );
            }
        }
    }

    private setHideSearchCustomerResult(hideResult = true) {
        this.setState({ hideResult });
    }

    private getCustomerExistingRelationships(): RelationshipType[] {
        const { data, existingMembers } = this.props;

        let existingRelationships = [];
        if (data?.CustomerId) {
            existingRelationships = (existingMembers || []).some((x) => x?.CustomerId == data?.CustomerId)
                ? (existingMembers || []).find((x) => x?.CustomerId == data?.CustomerId)?.Relationships
                : [];
        }
        return existingRelationships;
    }

    render() {
        const { data } = this.props;
        const options = [];

        const existingRelationships = this.getCustomerExistingRelationships();

        RelationshipTypeArray.forEach((type) => {
            options.push({
                label: RelationshipTypeMap[type],
                value: data?.Relationships.includes(type) || existingRelationships.includes(type),
                disabled: this.getConstraint(type, existingRelationships),
                onChange: (v) => {
                    if (v) {
                        const relationships = data?.Relationships;
                        if (!existingRelationships.includes(v)) {
                            relationships.push(type);
                            this.props.onChange({ Relationships: relationships });
                        }
                    } else {
                        let relationships = data?.Relationships;
                        relationships = relationships.filter((x) => x != type);
                        if (type == RelationshipType.IndividualTrustee) {
                            relationships = relationships.filter((x) => x != RelationshipType.Beneficiary);
                        }
                        if (relationships.length < 1) {
                            this.props.onChange({
                                Relationships: relationships,
                                ExistingCustomer: null,
                                CustomerId: null,
                                Company: null,
                                Individual: null,
                            });
                        }
                        this.props.onChange({ Relationships: relationships });
                    }
                },
            });
        });

        return (
            <>
                {this.state.showErrorMsg &&
                    this.state.showErrorMsg.map((ele, i) => (
                        <Alert alertType={AlertType.Error} message={ele} key={`error-${i}`} />
                    ))}

                <label>Relationship to corporate</label>
                {this.props.data.Relationships.includes(RelationshipType.Settlor) && (
                    <Alert alertType={AlertType.Warning} message={OrganisationMemberPopUpInfo.settlorToolTip} />
                )}
                <CheckboxGroup type="150" wrapCss="square-checkbox" options={options} />

                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label={"Add new/existing customer"}
                    selected={data?.ExistingCustomer}
                    options={[true, false]}
                    getText={(v) => (v ? "Existing" : "New")}
                    onChange={(v) => {
                        this.props.onChange({
                            ExistingCustomer: v,
                            CustomerId: null,
                            Company: { EmailAddressDetails: [] },
                            Individual: null,
                        });
                    }}
                />
                {data?.Relationships.length > 0 &&
                    !data?.Relationships.includes(RelationshipType.CorporateTrustee) &&
                    this.renderIndividualDetails()}
                {data?.Relationships.includes(RelationshipType.CorporateTrustee) && this.renderCompanyDetails()}

                <hr className="mt-40 mb-20" />
            </>
        );
    }
}
